import React from 'react';
import { Card, Row, Col, Statistic, Progress, Divider, Typography, Table, Tooltip } from 'antd';
import { 
  ClockCircleOutlined, 
  CheckCircleOutlined, 
  WarningOutlined,
  CarOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { OTR_THRESHOLDS } from '../utils/OTRAnalytics';

const { Title, Text } = Typography;

// Helper function to format time threshold
const formatTimeThreshold = (seconds) => {
  const absSeconds = Math.abs(seconds);
  if (absSeconds < 60) {
    return `${absSeconds} seconds`;
  }
  const minutes = Math.floor(absSeconds / 60);
  const remainingSeconds = absSeconds % 60;
  let result = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  if (remainingSeconds > 0) {
    result += ` ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`;
  }
  return seconds < 0 ? `${result}` : result;
};

/**
 * Get color based on OTR percentage
 * @param {number} percentage - OTR percentage
 * @returns {string} Color code
 */
const getOTRColor = (percentage) => {
  if (percentage >= 95) return '#52c41a'; // Green for excellent
  if (percentage >= 90) return '#73d13d'; // Light green for good
  if (percentage >= 85) return '#95de64'; // Lighter green for acceptable
  if (percentage >= 80) return '#bae7ff'; // Light blue for warning
  if (percentage >= 75) return '#faad14'; // Orange for concerning
  return '#f5222d'; // Red for poor
};

/**
 * OTR Summary Component
 * Displays On-Time Running summary statistics for Transport for NSW Trip Passing Time Reports
 */
const OTRSummary = ({ reportData }) => {
  // If no data, show empty state
  if (!reportData || !reportData.summary) {
    return (
      <Card title="On-Time Running Summary">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">No data available. Please generate a report.</Text>
        </div>
      </Card>
    );
  }

  const { 
    summary, 
    otrAnalysis,
    routePerformance = { routes: [] },
  } = reportData;

  // Format percentage values
  const formatPercent = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '0%';
    return `${Math.min(value, 100).toFixed(2)}%`;
  };

  // Default values for stop types if not available
  const getStopTypeData = (type) => {
    if (!otrAnalysis || !otrAnalysis.byStopType) return { stopType: type, totalStops: 0, onTimeCount: 0, otrPercentage: 0 };
    
    const stopTypeData = otrAnalysis.byStopType.find(s => s.stopType === type) || 
      { stopType: type, totalStops: 0, onTimeCount: 0, otrPercentage: 0 };
    
    return {
      ...stopTypeData,
      // Add derived fields for UI
      earlyCount: stopTypeData.earlyCount || Math.floor(stopTypeData.totalStops * (summary.earlyPercentage || 0) / 100) || 0,
      lateCount: stopTypeData.lateCount || Math.floor(stopTypeData.totalStops * (summary.latePercentage || 0) / 100) || 0,
      veryLateCount: stopTypeData.veryLateCount || Math.floor(stopTypeData.totalStops * (summary.veryLatePercentage || 0) / 100) || 0
    };
  };

  // Get stop type data
  const firstStopData = getStopTypeData('first');
  const middleStopData = getStopTypeData('middle');
  const lastStopData = getStopTypeData('last');

  // Top performing routes
  const topRoutes = [...(routePerformance.routes || [])]
    .sort((a, b) => b.overallOTR - a.overallOTR)
    .slice(0, 3);

  // Bottom performing routes
  const bottomRoutes = [...(routePerformance.routes || [])]
    .sort((a, b) => a.overallOTR - b.overallOTR)
    .slice(0, 3);

  // Default values for summary statistics
  const summaryStats = {
    overallOTR: summary.onTimePercentage || 0,
    totalTrips: summary.totalTrips || 0,
    uniqueRoutes: summary.uniqueRoutes || 0,
    noTimingPoints: summary.noTimingPoints || 0,
    onTimePercentage: summary.onTimePercentage || 0,
    earlyPercentage: summary.earlyPercentage || 0,
    latePercentage: summary.latePercentage || 0,
    veryLatePercentage: summary.veryLatePercentage || 0,
    onTimePoints: summary.onTimeCount || 0,
    earlyPoints: summary.earlyCount || 0,
    latePoints: summary.lateCount || 0,
    veryLatePoints: summary.veryLateCount || 0,
    trackedPercentage: summary.trackedPercentage || 0,
    trackedRecords: summary.trackedRecords || 0,
    untrackedRecords: summary.untrackedRecords || 0,
    trackedArrivalPercentage: summary.trackedArrivalPercentage || 0,
    trackedEarlyPercentage: summary.trackedEarlyPercentage || 0,
    trackedLatePercentage: summary.trackedLatePercentage || 0,
    trackedVeryLatePercentage: summary.trackedVeryLatePercentage || 0,
    trackedOnTimePoints: summary.trackedOnTimeCount || 0,
    trackedEarlyPoints: summary.trackedEarlyCount || 0,
    trackedLatePoints: summary.trackedLateCount || 0,
    trackedVeryLatePoints: summary.trackedVeryLateCount || 0,
    fullyTrackedTripsPercentage: summary.fullyTrackedTripsPercentage || 0,
    fullyTrackedTrips: summary.fullyTrackedTrips || 0
  };

  return (
    <div className="otr-summary">
      <Card title={<Title level={4}>On-Time Running Summary</Title>} className="summary-card">
        {/* Overall Statistics */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={4}>
            <Statistic 
              title={
                <Tooltip title="Overall On-Time Running percentage across all timing points">
                  Overall OTR
                </Tooltip>
              }
              value={summaryStats.overallOTR}
              precision={1}
              suffix="%"
              valueStyle={{ color: getOTRColor(summaryStats.overallOTR) }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Statistic
              title={
                <Tooltip title="Total number of scheduled trips in the report">
                  Scheduled Trips
                </Tooltip>
              }
              value={summaryStats.totalTrips}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Statistic
              title={
                <Tooltip title="Percentage of trips that have timing data for start and end stops">
                  Tracked Trips
                </Tooltip>
              }
              precision={1}
              value={summaryStats.fullyTrackedTrips || 0}
              suffix={` (${summaryStats.fullyTrackedTripsPercentage?.toFixed(1) || '0'}%)`}
              valueStyle={{ color: getOTRColor(summaryStats.fullyTrackedTripsPercentage) }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Statistic
              title={
                <Tooltip title="Total number of timing points in the report">
                  Total Timing Points
                </Tooltip>
              }
              value={summaryStats.noTimingPoints}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Statistic
              title={
                <Tooltip title="Number of timing points with actual arrival or departure data">
                  Tracked Timing Points
                </Tooltip>
              }
              value={summaryStats.trackedRecords}
              suffix={` (${summaryStats.trackedPercentage.toFixed(1)}%)`}
            />
          </Col>
        </Row>

        <Divider />

        {/* OTR Breakdown */}
        <Title level={5}>OTR Performance Breakdown</Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" title={
              <Tooltip title={`Percentage of stops that departed between ${formatTimeThreshold(OTR_THRESHOLDS.early)} and ${formatTimeThreshold(OTR_THRESHOLDS.onTime)} after the scheduled time`}>
                On Time
              </Tooltip>
            } className="statistic-card">
              <Statistic
                value={summaryStats.onTimePercentage}
                precision={1}
                suffix="%"
                valueStyle={{ color: '#52c41a' }}
              />
              <Text type="secondary">{summaryStats.onTimePoints} timing points</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" title={
              <Tooltip title={`Percentage of stops that departed more than ${formatTimeThreshold(OTR_THRESHOLDS.early)} before the scheduled time`}>
                Early
              </Tooltip>
            } className="statistic-card">
              <Statistic
                value={summaryStats.earlyPercentage}
                precision={1}
                suffix="%"
                valueStyle={{ color: '#1890ff' }}
              />
              <Text type="secondary">{summaryStats.earlyPoints} timing points</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" title={
              <Tooltip title={`Percentage of stops that departed between ${formatTimeThreshold(OTR_THRESHOLDS.onTime)} and ${formatTimeThreshold(OTR_THRESHOLDS.late)} after the scheduled time`}>
                Late
              </Tooltip>
            } className="statistic-card">
              <Statistic
                value={summaryStats.latePercentage}
                precision={1}
                suffix="%"
                valueStyle={{ color: '#faad14' }}
              />
              <Text type="secondary">{summaryStats.latePoints} timing points</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" title={
              <Tooltip title={`Percentage of stops that departed more than ${formatTimeThreshold(OTR_THRESHOLDS.late)} after the scheduled time`}>
                Very Late
              </Tooltip>
            } className="statistic-card">
              <Statistic
                value={summaryStats.veryLatePercentage}
                precision={1}
                suffix="%"
                valueStyle={{ color: '#f5222d' }}
              />
              <Text type="secondary">{summaryStats.veryLatePoints} timing points</Text>
            </Card>
          </Col>
        </Row>

        <Divider />

        {/* Stop Position Performance */}
        <Title level={5}>Performance by Stop Position</Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card size="small" title={
              <Tooltip title="On-Time Running performance for the first stop of each trip">
                First Stop OTR
              </Tooltip>
            } className="statistic-card">
              <Progress 
                type="circle"
                percent={parseFloat(firstStopData.onTimePercentage || 0).toFixed(2)}
                width={80}
                format={percent => `${percent}%`}
              />
              <Divider />
              <Row>
                <Col span={8}><Text type="secondary">Total:</Text></Col>
                <Col span={16}><Text>{firstStopData.totalStops}</Text></Col>
              </Row> 
              <Row>
                <Col span={8}><Text type="secondary">On Time:</Text></Col>
                <Col span={16}><Text>{firstStopData.onTimeCount} ({formatPercent(firstStopData.onTimePercentage)})</Text></Col>
              </Row>
              <Row>
                <Col span={8}><Text type="secondary">Early:</Text></Col>
                <Col span={16}><Text>{firstStopData.earlyCount} ({formatPercent(firstStopData.earlyPercentage || (firstStopData.totalStops > 0 ? (firstStopData.earlyCount / firstStopData.totalStops) * 100 : 0))})</Text></Col>
              </Row>
              <Row>
                <Col span={8}><Text type="secondary">Late:</Text></Col>
                <Col span={16}><Text>{firstStopData.lateCount} ({formatPercent(firstStopData.latePercentage || (firstStopData.totalStops > 0 ? (firstStopData.lateCount / firstStopData.totalStops) * 100 : 0))})</Text></Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" title={
              <Tooltip title="On-Time Running performance for stops in the middle of each trip">
                Middle Stop OTR
              </Tooltip>
            } className="statistic-card">
              <Progress 
                type="circle"
                percent={parseFloat(middleStopData.onTimePercentage || 0).toFixed(2)}
                width={80}
                format={percent => `${percent}%`}
              />
              <Divider />
              <Row>
                <Col span={8}><Text type="secondary">Total:</Text></Col>
                <Col span={16}><Text>{middleStopData.totalStops}</Text></Col>
              </Row>
              <Row>
                <Col span={8}><Text type="secondary">On Time:</Text></Col>
                <Col span={16}><Text>{middleStopData.onTimeCount} ({formatPercent(middleStopData.onTimePercentage)})</Text></Col>
              </Row>
              <Row>
                <Col span={8}><Text type="secondary">Early:</Text></Col>
                <Col span={16}><Text>{middleStopData.earlyCount} ({formatPercent(middleStopData.earlyPercentage || (middleStopData.totalStops > 0 ? (middleStopData.earlyCount / middleStopData.totalStops) * 100 : 0))})</Text></Col>
              </Row>
              <Row>
                <Col span={8}><Text type="secondary">Late:</Text></Col>
                <Col span={16}><Text>{middleStopData.lateCount} ({formatPercent(middleStopData.latePercentage || (middleStopData.totalStops > 0 ? (middleStopData.lateCount / middleStopData.totalStops) * 100 : 0))})</Text></Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" title={
              <Tooltip title="On-Time Running performance for the last stop of each trip">
                Last Stop OTR
              </Tooltip>
            } className="statistic-card">
              <Progress 
                type="circle"
                percent={parseFloat(lastStopData.onTimePercentage || 0).toFixed(2)}
                width={80}
                format={percent => `${percent}%`}
              />
              <Divider />
              <Row>
                <Col span={8}><Text type="secondary">Total:</Text></Col>
                <Col span={16}><Text>{lastStopData.totalStops}</Text></Col>
              </Row>
              <Row>
                <Col span={8}><Text type="secondary">On Time:</Text></Col>
                <Col span={16}><Text>{lastStopData.onTimeCount} ({formatPercent(lastStopData.onTimePercentage)})</Text></Col>
              </Row>
              <Row>
                <Col span={8}><Text type="secondary">Early:</Text></Col>
                <Col span={16}><Text>{lastStopData.earlyCount} ({formatPercent(lastStopData.earlyPercentage || (lastStopData.totalStops > 0 ? (lastStopData.earlyCount / lastStopData.totalStops) * 100 : 0))})</Text></Col>
              </Row>
              <Row>
                <Col span={8}><Text type="secondary">Late:</Text></Col>
                <Col span={16}><Text>{lastStopData.lateCount} ({formatPercent(lastStopData.latePercentage || (lastStopData.totalStops > 0 ? (lastStopData.lateCount / lastStopData.totalStops) * 100 : 0))})</Text></Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Divider />

        {/* Top and Bottom Routes */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title={
              <Tooltip title="Routes with the highest On-Time Running performance">
                Top Performing Routes
              </Tooltip>
            } size="small">
              {topRoutes.length > 0 ? (
                <Table 
                  dataSource={topRoutes.map((route, index) => ({ ...route, key: `top-${index}` }))}
                  columns={[
                    { title: 'Route', dataIndex: 'route', key: 'route' },
                    { 
                      title: 'OTR', 
                      dataIndex: 'overallOTR', 
                      key: 'overallOTR',
                      render: (val) => formatPercent(val)
                    },
                    { title: 'Trips', dataIndex: 'totalTrips', key: 'totalTrips' }
                  ]}
                  pagination={false}
                  size="small"
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text type="secondary">No route data available</Text>
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title={
              <Tooltip title="Routes with the lowest On-Time Running performance">
                Bottom Performing Routes
              </Tooltip>
            } size="small">
              {bottomRoutes.length > 0 ? (
                <Table 
                  dataSource={bottomRoutes.map((route, index) => ({ ...route, key: `bottom-${index}` }))}
                  columns={[
                    { title: 'Route', dataIndex: 'route', key: 'route' },
                    { 
                      title: 'OTR', 
                      dataIndex: 'overallOTR', 
                      key: 'overallOTR',
                      render: (val) => formatPercent(val)
                    },
                    { title: 'Trips', dataIndex: 'totalTrips', key: 'totalTrips' }
                  ]}
                  pagination={false}
                  size="small"
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text type="secondary">No route data available</Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default OTRSummary; 